/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { retailAction } from '@msdyn365-commerce/retail-proxy';

import { addToCartAsyncInput } from './DataActionExtension.g';
import { ILobasCartStagingHandoffConfig } from '../modules/lobas-cart-staging-handoff/lobas-cart-staging-handoff.props.autogenerated';

/**
 * GetCartStagingData Input Action
 *
 * The cart is deliberately NOT part of this input. It used to be read straight out of the
 * `_msdyn365___cart_` cookie, but that cookie is not the last word on which cart the customer is
 * looking at: for a signed-in customer still carrying an anonymous cart, the SDK moves those lines into
 * the customer's own cart and repoints the cookie during the same render. A handoff written against the
 * raw cookie value can land in the cart being abandoned, which is the likeliest reason a door staged on
 * 2026-09-15 never turned up in the customer's cart. The cart is resolved inside the action instead,
 * from the same cart state the header and the cart page read.
 */
export class GetCartStagingDataInput implements Msdyn365.IActionInput {
    public intentId: string;

    public constructor(intentId: string) {
        this.intentId = intentId;
    }

    public getCacheKey = () => `CartStagingHandoff:${this.intentId}`;
    public getCacheObjectType = () => 'CartStagingHandoff';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

const getIntentId = (ctx: Msdyn365.ICreateActionContext, paramName: string): string => {
    const raw = ctx.requestContext?.url?.requestUrl?.search || '';
    const sp = new URLSearchParams(raw.startsWith('?') ? raw : `?${raw}`);
    return (sp.get(paramName) || '').trim();
};

const createInput = (args: Msdyn365.ICreateActionContext<ILobasCartStagingHandoffConfig>): Msdyn365.IActionInput => {
    const stagingParamName = (args.config?.intentQueryParam as string) || 'intentId';
    const intentId = getIntentId(args, stagingParamName);

    if (!intentId) {
        throw new Error(`Missing query param: ${stagingParamName}`);
    }

    // Without consent the SDK cannot keep a cart cookie, so a cart resolved below would be forgotten on
    // the next page. Say that here rather than handing the configuration to a cart that gets lost.
    if (!args.requestContext.cookies.isConsentGiven()) {
        throw new Error('Cookie consent not given');
    }

    return new GetCartStagingDataInput(intentId);
};

async function action(input: GetCartStagingDataInput, ctx: Msdyn365.IActionContext): Promise<string> {
    // The same cart the rest of the page uses: getCartState reads the cookie, falls back to the
    // customer's own cart, claims an anonymous one after sign-in, and creates a cart when there is none.
    const cartState = await getCartState(ctx);
    const cartId = cartState?.cart?.Id;

    if (!cartId) {
        throw new Error('Cart staging handoff: no active cart could be resolved');
    }

    const handedToCartId = await retailAction<string>(addToCartAsyncInput(input.intentId, cartId), ctx);

    // The Scale Unit wrote the lines straight onto the cart, so the state this page renders from is now
    // a version behind. The redirect reloads everything anyway; this keeps the page honest if it cannot.
    try {
        await cartState.refreshCart({});
    } catch (error) {
        ctx.telemetry.warning(`Cart staging handoff: refreshing the cart after the handoff failed. ${error}`);
    }

    return handedToCartId;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<string>>action,
    id: 'GetCartStagingData',
    input: createInput
});
